import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** Ordered degree levels for MINIMUM requirement (Doctorate satisfies Master’s, etc.) */
export const DEGREE_LEVELS = [
  "None",
  "High School / GED",
  "Associate",
  "Bachelor’s",
  "Master’s",
  "Doctorate / PhD",
] as const;
export type DegreeLevel = (typeof DEGREE_LEVELS)[number];

/** Common healthcare & general qualifications. Extend as needed. */
export const QUALIFICATION_OPTIONS = [
  // General
  "Background Check Clearance",
  "Valid Driver’s License",
  "Eligible to Work in the U.S.",
  "Fluent in English",
  "Fluent in Other Language(s)",
  // Healthcare / Caregiving
  "Certified Nursing Assistant (CNA)",
  "Home Health Aide (HHA)",
  "Licensed Practical Nurse (LPN)",
  "Registered Nurse (RN)",
  "CPR / First Aid Certification",
  "Medication Administration Training",
  "Food Handling Certification",
  "TB Test / Health Clearance",
  "Prior Caregiving Experience",
] as const;

export type CertLogic = "ALL" | "ANY";

/** Shape stored in form state if you decide to persist later as JSON. */
export interface QualificationRule {
  degreeMin: DegreeLevel;
  certs: string[];
  certLogic: CertLogic;
}

export const DEFAULT_QUAL_RULE: QualificationRule = {
  degreeMin: "None",
  certs: [],
  certLogic: "ANY",
};

/** Build a succinct, human-friendly summary (pasted into Description). */
export function buildQualificationSummary(rule: QualificationRule): string {
  const parts: string[] = [];
  parts.push(
    rule.degreeMin !== "None"
      ? `Minimum degree: ${rule.degreeMin}`
      : "No minimum degree required"
  );
  if (rule.certs.length) {
    const logicWord = rule.certLogic === "ALL" ? "All of" : "Any of";
    parts.push(`${logicWord} the following: ${rule.certs.join(", ")}`);
  } else {
    parts.push("No specific certifications required");
  }
  return parts.join(" • ");
}

type Props = {
  value?: QualificationRule;
  onChange?: (next: QualificationRule) => void;
  /** Single action button callback after the summary */
  onGenerateSummary?: (summary: string) => void;
  disabled?: boolean;
  actionLabel?: string; // default: "Copy & paste into Description"
  /** NEW: start expanded/collapsed */
  defaultExpanded?: boolean; // default true
};

/**
 * Collapsible "Required Qualifications"
 * - Wrapped in MUI Accordion (collapsible)
 * - Controlled with `value` and `onChange`
 * - Emits a summary via `onGenerateSummary`
 */
export default function QualificationSection({
  value = DEFAULT_QUAL_RULE,
  onChange,
  onGenerateSummary,
  disabled,
  actionLabel = "Copy & paste into Description",
  defaultExpanded = false,
}: Props) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  const handleDegreeMin = (next: DegreeLevel) => {
    onChange?.({ ...value, degreeMin: next });
  };

  const toggleCert = (cert: string) => {
    const exists = value.certs.includes(cert);
    const next = exists
      ? value.certs.filter((c) => c !== cert)
      : [...value.certs, cert];
    onChange?.({ ...value, certs: next });
  };

  const handleCertLogic = (
    _: React.MouseEvent<HTMLElement>,
    next: CertLogic | null
  ) => {
    if (!next) return;
    onChange?.({ ...value, certLogic: next });
  };

  const summary = buildQualificationSummary(value);
const copyAndNotify = async () => {
  try {
    await navigator.clipboard.writeText(summary);
  } catch {}
  try {
    window.dispatchEvent(
      new CustomEvent("plany:add-qual-summary", { detail: { summary } })
    );
  } catch {}
  onGenerateSummary?.(summary); // keep your existing callback if you use it
};

return (
  <Accordion
    expanded={expanded}
    onChange={(_, open) => setExpanded(open)}
    disableGutters
    sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="subtitle1" fontWeight={700}>
        Required Qualifications
      </Typography>
    </AccordionSummary>

    <AccordionDetails>
      <Stack spacing={2}>
        {/* Degree minimum */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Minimum Degree Requirement
          </Typography>

          <RadioGroup
            row
            value={value.degreeMin === "None" ? "none" : "hasmin"}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "none") handleDegreeMin("None");
              else if (value.degreeMin === "None")
                handleDegreeMin("High School / GED");
            }}
          >
            <FormControlLabel
              value="none"
              control={<Radio disabled={disabled} />}
              label="No minimum degree"
            />
            <FormControlLabel
              value="hasmin"
              control={<Radio disabled={disabled} />}
              label="Set minimum degree"
            />
          </RadioGroup>

          {value.degreeMin !== "None" && (
            <FormControl
              size="small"
              sx={{ mt: 1, minWidth: 260 }}
              disabled={disabled}
            >
              <InputLabel id="degree-min-label">Minimum Degree</InputLabel>
              <Select
                labelId="degree-min-label"
                label="Minimum Degree"
                value={value.degreeMin}
                onChange={(e) => handleDegreeMin(e.target.value as DegreeLevel)}
              >
                {DEGREE_LEVELS.filter((d) => d !== "None").map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Alert severity="info" sx={{ mt: 1 }}>
            <strong>Note:</strong> Choosing <em>Master’s</em> also accepts{" "}
            <em>Doctorate/PhD</em>.
          </Alert>
        </Box>

        <Divider />

        {/* Certifications block */}
        <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">
              Certifications &amp; Other Requirements
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Match logic:
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={value.certLogic}
                onChange={handleCertLogic}
                disabled={disabled}
              >
                <ToggleButton value="ANY">Any of selected</ToggleButton>
                <ToggleButton value="ALL">All selected</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          <FormGroup
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 0.5,
            }}
          >
            {QUALIFICATION_OPTIONS.map((q) => (
              <FormControlLabel
                key={q}
                control={
                  <Checkbox
                    checked={value.certs.includes(q)}
                    onChange={() => toggleCert(q)}
                    disabled={disabled}
                  />
                }
                label={q}
              />
            ))}
          </FormGroup>
        </Box>

        {/* Summary with action */}
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Summary (copy &amp; paste into Description)
          </Typography>

          <Alert
            severity="success"
            icon={false}
            sx={{
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={copyAndNotify}
                disabled={disabled}
              >
                {actionLabel}
              </Button>
            }
          >
            {summary}
          </Alert>

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}
          >
            {value.degreeMin !== "None" && (
              <Chip
                size="small"
                label={`Min: ${value.degreeMin}`}
                color="primary"
                variant="outlined"
              />
            )}
            {value.certs.map((c) => (
              <Chip key={c} size="small" label={c} variant="outlined" />
            ))}
            {!value.certs.length && (
              <Chip size="small" label="No extra certs" variant="outlined" />
            )}
          </Stack>
        </Box>
      </Stack>
    </AccordionDetails>
  </Accordion>
);
}
